import { useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import type { CoverageMapResponse, CoveragePostcode } from "@breeze/shared";

// Tech Coverage map (Phase 2).
//
// One CircleMarker per outward postcode. Two visual modes:
//   - "activity": radius scales with last-90d job count, fill colour by
//     revenue intensity. Quickly answers "where are we busy".
//   - "tech": every covered outward shaded with the assigned tech's colour;
//     uncovered outwards rendered as faint grey hollow rings. Quickly
//     answers "where do techs claim coverage".
//
// On click an outward selects it and surfaces a side panel with the
// breakdown — handled by the parent via onSelect.

const TECH_PALETTE = [
  "#36C5F0", "#ECB22E", "#2EB67D", "#F07B3F",
  "#7F6DC5", "#E01E5A", "#A678A8", "#611F69",
];

export type CoverageMapMode = "activity" | "tech";

type Props = {
  data: CoverageMapResponse;
  mode: CoverageMapMode;
  selectedOutward: string | null;
  onSelect: (outward: string | null) => void;
};

export function CoverageMap({ data, mode, selectedOutward, onSelect }: Props): JSX.Element {
  // Map technician_id → palette colour (stable across renders).
  const techColor = useMemo(() => {
    const m = new Map<number, string>();
    data.techs.forEach((t, i) => {
      m.set(t.technicianId, TECH_PALETTE[i % TECH_PALETTE.length] ?? "#A678A8");
    });
    return m;
  }, [data.techs]);

  const maxRevenue = useMemo(() => {
    let max = 0;
    for (const p of data.postcodes) if (p.revenue > max) max = p.revenue;
    return max;
  }, [data.postcodes]);

  const maxJobs = useMemo(() => {
    let max = 0;
    for (const p of data.postcodes) if (p.jobCount > max) max = p.jobCount;
    return max;
  }, [data.postcodes]);

  // Default centre London. The map renders fine even when a postcode is
  // outside this view — user can pan / zoom out to find the rest.
  const center: [number, number] = [51.5074, -0.1278];

  return (
    <div className="h-[480px] w-full overflow-hidden rounded-lg border border-ink-700">
      <MapContainer
        center={center}
        zoom={9}
        scrollWheelZoom={true}
        style={{ height: "100%", width: "100%", background: "#f5f3ee" }}
      >
        {/* Default OpenStreetMap tiles — switched from CARTO Dark Matter
            (unreadable labels) and from CARTO Voyager (wrong URL path
            returned blank tiles). */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {data.postcodes.map((p) => (
          <PostcodeMarker
            key={p.outward}
            p={p}
            mode={mode}
            techColor={techColor}
            maxJobs={maxJobs}
            maxRevenue={maxRevenue}
            isSelected={selectedOutward === p.outward}
            onSelect={onSelect}
          />
        ))}
      </MapContainer>
    </div>
  );
}

function PostcodeMarker({
  p,
  mode,
  techColor,
  maxJobs,
  maxRevenue,
  isSelected,
  onSelect,
}: {
  p: CoveragePostcode;
  mode: CoverageMapMode;
  techColor: Map<number, string>;
  maxJobs: number;
  maxRevenue: number;
  isSelected: boolean;
  onSelect: (outward: string | null) => void;
}): JSX.Element {
  const radius = computeRadius(p, mode, maxJobs);
  const { fill, stroke, fillOpacity } = computeColors(p, mode, techColor, maxRevenue, isSelected);
  return (
    <CircleMarker
      center={[p.latitude, p.longitude]}
      radius={radius}
      pathOptions={{
        color: stroke,
        fillColor: fill,
        fillOpacity,
        weight: isSelected ? 3 : 1,
      }}
      eventHandlers={{
        click: () => onSelect(isSelected ? null : p.outward),
      }}
    >
      <Tooltip direction="top" offset={[0, -4]} opacity={0.95}>
        <div style={{ fontSize: 12, lineHeight: 1.4 }}>
          <div style={{ fontWeight: 600 }}>{p.outward}</div>
          <div>£{p.revenue.toLocaleString("en-GB", { maximumFractionDigits: 0 })} · {p.jobCount} jobs (90d)</div>
          {p.techIds.length > 0 ? (
            <div style={{ color: "#aaa" }}>{p.techIds.length} tech{p.techIds.length === 1 ? "" : "s"} cover</div>
          ) : (
            <div style={{ color: "#E01E5A" }}>uncovered</div>
          )}
        </div>
      </Tooltip>
    </CircleMarker>
  );
}

function computeRadius(p: CoveragePostcode, mode: CoverageMapMode, maxJobs: number): number {
  if (mode === "tech") {
    // In tech mode, marker size doesn't carry job info — keep all the
    // same so colour is the only signal. Slightly bigger for covered.
    return p.techIds.length > 0 ? 6 : 4;
  }
  // Activity mode: sqrt scaling so a 100-job outward isn't 100× bigger
  // than a 1-job one — keeps the map legible.
  if (maxJobs === 0) return 4;
  const t = Math.sqrt(p.jobCount / maxJobs);
  return 4 + t * 16;
}

function computeColors(
  p: CoveragePostcode,
  mode: CoverageMapMode,
  techColor: Map<number, string>,
  maxRevenue: number,
  isSelected: boolean
): { fill: string; stroke: string; fillOpacity: number } {
  // Dark slate stroke for selected outwards — visible on the cream Voyager
  // base. Neutral fill for "no data" tuned for light bg.
  const selectedStroke = "#0f172a"; // slate-900
  const NEUTRAL_FILL = "#64748b";   // slate-500
  const NEUTRAL_STROKE = "#475569"; // slate-600
  if (mode === "tech") {
    if (p.techIds.length === 0) {
      // Uncovered — bright but subordinate ring.
      return { fill: NEUTRAL_FILL, stroke: isSelected ? selectedStroke : NEUTRAL_STROKE, fillOpacity: 0.4 };
    }
    // Covered — pick the colour of the first tech (good enough — overlap
    // is rare and tooltip shows full count). Multi-tech overlap could
    // be visualised with alternating rings later if it matters.
    const firstId = p.techIds[0];
    const color = (firstId !== undefined ? techColor.get(firstId) : undefined) ?? NEUTRAL_FILL;
    return {
      fill: color,
      stroke: isSelected ? selectedStroke : color,
      fillOpacity: 0.7,
    };
  }
  // Activity mode: revenue intensity as a green→amber→red ramp.
  if (p.jobCount === 0) {
    return { fill: NEUTRAL_FILL, stroke: isSelected ? selectedStroke : NEUTRAL_STROKE, fillOpacity: 0.4 };
  }
  if (maxRevenue === 0) {
    return { fill: "#36C5F0", stroke: isSelected ? selectedStroke : "#36C5F0", fillOpacity: 0.55 };
  }
  const t = Math.min(1, p.revenue / maxRevenue);
  // Stage palette: cyan → green → amber → red.
  const fill = t < 0.33 ? "#36C5F0" : t < 0.66 ? "#2EB67D" : t < 0.85 ? "#ECB22E" : "#E01E5A";
  return { fill, stroke: isSelected ? selectedStroke : fill, fillOpacity: 0.6 };
}
